import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import { formatApiError } from "../lib/utils-extra";

export default function NewEvaluation() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [employee, setEmployee] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [evalDate, setEvalDate] = useState(today);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState(today);
  const [template, setTemplate] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/employees").then(({ data }) => setEmployees(data));
    // Default period: last quarter
    const d = new Date();
    d.setDate(d.getDate() - 90);
    setPeriodStart(d.toISOString().slice(0, 10));
  }, []);

  useEffect(() => {
    if (!employeeId) { setEmployee(null); setTemplate(null); return; }
    api.get(`/employees/${employeeId}`).then(({ data }) => {
      setEmployee(data);
      if (data.position_id) {
        api.get(`/kpi-templates/by-position/${data.position_id}`).then(({ data: t }) => setTemplate(t));
      } else { setTemplate(null); }
    });
  }, [employeeId]);

  const start = async () => {
    if (!employeeId) return toast.error("Select an employee");
    if (!template) return toast.error("This employee's position has no KPI template");
    setBusy(true);
    try {
      const { data } = await api.post("/evaluations", {
        employee_id: employeeId,
        template_id: template.id,
        evaluation_date: evalDate,
        period_start: periodStart,
        period_end: periodEnd,
      });
      toast.success("Evaluation started");
      navigate(`/evaluations/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="new-evaluation-page">
      <button onClick={() => navigate(-1)} className="text-sm text-[#475569] flex items-center gap-1">
        <ArrowLeft size={14} /> Back
      </button>
      <div>
        <div className="small-label">Step 1 of 2</div>
        <h1 className="font-display text-3xl font-semibold mt-1">New Evaluation</h1>
        <p className="text-[#475569] text-sm mt-1">Pick an employee and set the period.</p>
      </div>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-6 space-y-5">
          <div>
            <Label className="small-label">Employee</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger className="mt-2 h-11" data-testid="select-employee"><SelectValue placeholder="Select an employee" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.full_name} — {e.position_name || "No position"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {employee && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4 rounded-xl bg-[#F1F5F9] border border-[#E2E8F0]">
              <Info label="Position" value={employee.position_name || "—"} />
              <Info label="Department" value={employee.department || "—"} />
              <Info label="Frequency" value={(employee.evaluation_frequency || "").replace("_", " ")} />
              <Info label="Template" value={template ? template.name : "Missing"}
                    danger={!template} />
              <Info label="# KPIs" value={template ? template.lines?.length : "—"} />
              <Info label="Next due" value={employee.next_evaluation_date || "—"} />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="small-label">Evaluation Date</Label>
              <Input type="date" className="mt-2" value={evalDate} onChange={(e) => setEvalDate(e.target.value)} data-testid="eval-date" />
            </div>
            <div>
              <Label className="small-label">Period Start</Label>
              <Input type="date" className="mt-2" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} data-testid="period-start" />
            </div>
            <div>
              <Label className="small-label">Period End</Label>
              <Input type="date" className="mt-2" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} data-testid="period-end" />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
            <Button onClick={start} disabled={busy || !employeeId || !template}
                    data-testid="start-evaluation-button"
                    className="bg-[#0EA5E9] hover:bg-[#0284C7]">
              {busy ? "Starting…" : "Start evaluation →"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Info({ label, value, danger }) {
  return (
    <div>
      <div className="small-label">{label}</div>
      <div className={`font-medium text-sm mt-1 ${danger ? "text-[#DC2626]" : ""}`}>{value}</div>
    </div>
  );
}
